import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtSize, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "@/components/bits";
import { Plus, Save, Trash2, GripVertical, Film } from "lucide-react";

export default function Playlists({ embedded = false }) {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [playlists, setPlaylists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [media, setMedia] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [newName, setNewName] = useState("");
  const [name, setName] = useState("");
  const [items, setItems] = useState([]);
  const [addId, setAddId] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadLists = useCallback(() => {
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadLists();
    if (isSuper) {
      api.get("/tenants").then((r) => {
        setTenants(r.data);
        if (r.data.length) setTenantId((cur) => cur || r.data[0].id);
      }).catch(() => {});
    }
  }, [isSuper, loadLists]);

  async function select(id) {
    try {
      const { data } = await api.get(`/playlists/${id}`);
      setSelected(data);
      setName(data.name);
      setItems(data.items || []);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function create() {
    if (!newName.trim()) return;
    try {
      const payload = { name: newName.trim() };
      if (isSuper) payload.tenant_id = tenantId;
      const { data } = await api.post("/playlists", payload);
      setNewName("");
      toast.success("Wiedergabeliste angelegt");
      loadLists();
      select(data.id);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function save() {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/playlists/${selected.id}`, {
        name,
        items: items.map((it) => ({ media_id: it.media_id, duration_sec: Number(it.duration_sec) || 10 })),
      });
      toast.success(`Gespeichert — Version ${data.version}. Geräte synchronisieren automatisch.`);
      loadLists();
      select(selected.id);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  }

  async function removePlaylist() {
    if (!selected) return;
    try {
      await api.delete(`/playlists/${selected.id}`);
      toast.success("Wiedergabeliste gelöscht");
      setSelected(null);
      loadLists();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  function addItem() {
    const m = media.find((x) => x.id === addId);
    if (!m) return;
    setItems((arr) => [...arr, { media_id: m.id, duration_sec: 10, media: m }]);
    setAddId("");
  }

  function move(from, to) {
    if (from === to || from == null) return;
    setItems((arr) => {
      const next = [...arr];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  }

  return (
    <div data-testid="playlists-page">
      {!embedded && (
        <PageHeader title="Wiedergabelisten" sub="Reihenfolge per Drag-and-drop, Dauer pro Slide, versioniert" />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <div className="panel p-4 space-y-2" data-testid="playlist-create-panel">
            <div className="mono-label">Neue Wiedergabeliste</div>
            {isSuper && (
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="input-dark" data-testid="playlist-tenant-select">
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="input-dark flex-1"
                placeholder="Name, z. B. Schaufenster Frühling"
                data-testid="playlist-name-input"
              />
              <button onClick={create} className="btn-primary !px-3" data-testid="playlist-create-button">
                <Plus size={15} />
              </button>
            </div>
          </div>

          <div className="space-y-2" data-testid="playlist-list">
            {playlists.length ? (
              playlists.map((p) => (
                <button
                  key={p.id}
                  onClick={() => select(p.id)}
                  className={`w-full text-left panel p-4 transition-colors duration-150 ${
                    selected?.id === p.id ? "border-cyan-500/60 bg-cyan-500/5" : "hover:border-cyan-500/30"
                  }`}
                  data-testid={`playlist-item-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-200 font-medium">{p.name}</span>
                    <span className="font-mono text-[10px] text-cyan-400/80" title="Version — wird bei jeder Änderung hochgezählt; Geräte laden die Playlist dann automatisch neu">v{p.version}</span>
                  </div>
                  <div className="font-mono text-[10px] text-slate-400 mt-1">{p.items?.length || 0} Slides</div>
                </button>
              ))
            ) : (
              <EmptyState text="Noch keine Wiedergabelisten" testid="playlists-empty" />
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="panel p-5 space-y-5" data-testid="playlist-editor">
              <div className="flex items-center justify-between gap-3">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-dark flex-1 font-display font-semibold"
                  data-testid="playlist-rename-input"
                />
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="playlist-save-button">
                  <Save size={14} /> {saving ? "Speichere…" : "Speichern"}
                </button>
                <button onClick={removePlaylist} className="btn-ghost hover:!border-red-500/50 hover:!text-red-400" data-testid="playlist-delete-button">
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="flex gap-2">
                <select value={addId} onChange={(e) => setAddId(e.target.value)} className="input-dark flex-1" data-testid="playlist-add-media-select">
                  <option value="">— Medium hinzufügen —</option>
                  {media.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.filename} ({fmtSize(m.size_bytes)})
                    </option>
                  ))}
                </select>
                <button onClick={addItem} disabled={!addId} className="btn-ghost" data-testid="playlist-add-item-button">
                  <Plus size={14} /> Hinzufügen
                </button>
              </div>

              <div className="space-y-2" data-testid="playlist-items">
                {items.length ? (
                  items.map((it, i) => (
                    <div
                      key={`${it.media_id}-${i}`}
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        move(dragIdx, i);
                        setDragIdx(null);
                      }}
                      className={`flex items-center gap-3 border rounded-sm p-2.5 bg-black/20 transition-colors duration-150 ${
                        dragIdx === i ? "border-cyan-400/70 opacity-60" : "border-border hover:border-cyan-500/30"
                      }`}
                      data-testid={`playlist-entry-${i}`}
                    >
                      <GripVertical size={14} className="text-slate-400 cursor-grab shrink-0" />
                      <span className="font-mono text-[10px] text-slate-400 w-5">{String(i + 1).padStart(2, "0")}</span>
                      <div className="w-16 h-10 bg-black rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
                        {it.media?.mime?.startsWith("video/") ? (
                          <Film size={14} className="text-cyan-400" />
                        ) : it.media?.url ? (
                          <img src={fileUrl(it.media.url)} alt="" className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-200 truncate">{it.media?.filename || it.media_id}</div>
                        <div className="font-mono text-[10px] text-slate-400">{it.media?.mime}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          type="number"
                          min="1"
                          max="3600"
                          value={it.duration_sec}
                          onChange={(e) =>
                            setItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, duration_sec: e.target.value } : x)))
                          }
                          className="input-dark !w-20 !py-1 text-center font-mono text-xs"
                          data-testid={`playlist-item-duration-${i}`}
                        />
                        <span className="font-mono text-[10px] text-slate-400">sek</span>
                      </div>
                      <button
                        onClick={() => setItems((arr) => arr.filter((_, xi) => xi !== i))}
                        className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                        data-testid={`playlist-item-remove-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))
                ) : (
                  <EmptyState text="Leere Playlist — Medium hinzufügen" testid="playlist-items-empty" />
                )}
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Speichern erhöht die Version bei Inhaltsänderung — verbundene Geräte laden das neue Manifest
                automatisch und wechseln atomar (kein halb geladener Zustand).
              </p>
            </div>
          ) : (
            <EmptyState text="Wiedergabeliste auswählen oder neu anlegen" testid="playlist-no-selection" />
          )}
        </div>
      </div>
    </div>
  );
}
